import { axios } from './axiosClient';
import { z } from 'zod';
import { validate } from '@/utils/errors/helpers';
import {
  getPriceCatalogsSchema,
  priceCatalogSchema,
} from '@/types/schemas/prices/priceCatalogsSchema';

import {
  getPriceCatalogOrderDetailsSchema,
  getPriceCatalogsOrderSchema,
  getPricingPolicyMatricesSchema,
  getPricingPolicyMatrixDetailsSchema,
  pricePolicySchema,
  pricingPolicyMatrixSchema,
} from '@/types/schemas/prices/pricingPolicySchemas';

import { type TableFilterData } from '@/types/Base';
import type {
  PriceCatalog,
  PriceCatalogDetail,
  PricingPoliciesCatalogOrderDetail,
  PricingPolicy,
  PricingPolicyCatalogOrderHeader,
  PricingPolicyMatrix,
  PricingPolicyMatrixDetail,
} from '@/types/PricingTypes';

import { getPriceCatalogDetailsSchema } from '@/types/schemas/prices/priceCatalogDetailsSchema';
import { vatFactorSchema } from '@/types/schemas/vat/vatFactorSchema';
import type { VatFactor } from '@/types/Vat';

export async function addOrEditPriceCatalog(priceCatalog: PriceCatalog) {
  const response: any = await axios.post('/v2/prices/price-catalogs', priceCatalog);
  if (response.status === 201) return true;
  return false;
}

export async function getPriceCatalogById(id: string) {
  const response = await axios.get(`/v2/prices/price-catalogs/${id}`, {
    validateStatus: (status: number) => status === 204 || status == 200,
  });
  if (response.status === 204) return null;
  return validate(priceCatalogSchema, response.data);
}

export async function getPriceCatalogs() {
  const response = await axios.get(`/v2/prices/price-catalogs`);
  if (response) return validate(getPriceCatalogsSchema, response.data.data);
  return null;
}

export async function removePriceCatalogsByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/prices/price-catalogs/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getPricingPolicyCatalogsOrder() {
  const response = await axios.get(`/v2/prices/price-catalog-orders`);
  if (response) return validate(getPriceCatalogsOrderSchema, response.data.data);
  return null;
}

export async function removePricingPolicyCatalogsOrderByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/prices/price-catalog-orders/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function addOrEditPricingPolicyCatalogsOrder(
  catalogOrders: PricingPolicyCatalogOrderHeader[]
) {
  const response: any = await axios.post('/v2/prices/price-catalog-orders', catalogOrders);
  if (response.status === 201) return true;
  return false;
}

export async function getPricingPolicyCatalogsOrderDetails() {
  const response = await axios.get(`/v2/prices/price-catalog-order/details`);
  if (response) return validate(getPriceCatalogOrderDetailsSchema, response.data.data);
  return null;
}

export async function removePricingPolicyCatalogOrderDetailsByIds(
  ids: string[]
): Promise<string | null> {
  const response = await axios.post('/v2/prices/price-catalog-order/details/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function addOrEditPricingPolicyCatalogOrderDetails(
  catalogOrderDetails: PricingPoliciesCatalogOrderDetail[]
) {
  const response: any = await axios.post(
    '/v2/prices/price-catalog-order/details',
    catalogOrderDetails
  );
  if (response.status === 201) return true;
  return false;
}

export async function addOrEditPriceCatalogDetails(priceCatalogDetails: PriceCatalogDetail[]) {
  const response: any = await axios.post('/v2/prices/price-catalogs/details', priceCatalogDetails);
  if (response.status === 201) return true;
  return false;
}

export async function addPriceCatalogDetailsAllMaterials(
  priceCatalogId: string,
  includeVat: boolean,
  defaultPrice: number,
  allowDiscount: boolean,
  query: string | undefined
) {
  const response = await axios.post(
    '/v2/prices/price-catalogs/details/all-materials',
    { priceCatalogId, includeVat, defaultPrice, allowDiscount, query },
    {
      validateStatus: (status: number) => status === 400 || status == 201,
    }
  );
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  return true;
}

export async function removePriceCatalogDetailsByPriceCatalogId(
  priceCatalogId: string
): Promise<boolean> {
  const response = await axios.post(
    `/v2/prices/price-catalogs/details/delete-all/${priceCatalogId}`,
    {},
    {
      validateStatus: (status: number) => status === 400 || status == 204,
    }
  );
  if (response.status === 400) return false;
  return true;
}

export async function getPriceCatalogDetailsWithConditions({
  page = 1,
  itemsPerPage = 0,
  query,
  sortColumn,
  sortAscending,
  excludeIDs,
  priceCatalogId,
  groupItemIds,
}: TableFilterData & { priceCatalogId?: string; groupItemIds?: string[] }): Promise<{
  data: PriceCatalogDetail[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/prices/price-catalogs/details/search?take=${itemsPerPage}&skip=${skip}`,
    {
      searchText: query,
      sortColumn,
      sortAscending,
      excludeIDs,
      priceCatalogId,
      groupItemIds,
    }
  );

  const valid = validate(getPriceCatalogDetailsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function addOrEditPricingPolicyMatrix(matrix: PricingPolicyMatrix) {
  const response: any = await axios.post('/v2/prices/price-discount-matrices', matrix);
  if (response.status === 201) return true;
  return false;
}

export async function getPricingPolicyMatrices() {
  const response = await axios.get(`/v2/prices/price-discount-matrices`);
  if (response) return validate(getPricingPolicyMatricesSchema, response.data.data);
  return null;
}

export async function removeMatricesByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/prices/price-discount-matrices/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getPricingPolicyMatrixById(id: string): Promise<PricingPolicyMatrix | null> {
  const response = await axios.get(`/v2/prices/price-discount-matrices/${id}`, {
    validateStatus: (status: number) => status === 404 || status == 200,
  });
  if (response.status === 404) return null;
  return validate(pricingPolicyMatrixSchema, response.data);
}

export async function getPricingPolicyMatrixDetailsWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  matrixId,
}: TableFilterData & { matrixId: string }): Promise<{
  data: PricingPolicyMatrixDetail[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const url = `/v2/prices/price-discount-matrices/details/search?take=${itemsPerPage}&skip=${skip}&matrixHeaderId=${matrixId}`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    isActive,
  });
  const valid = validate(getPricingPolicyMatrixDetailsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function removePricingPolicyMatrixDetailsByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post(
    '/v2/prices/price-discount-matrices/details/delete-by-ids',
    ids,
    {
      validateStatus: (status: number) => status === 400 || status == 204,
    }
  );
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function addOrEditPricingPolicyMatrixDetails(
  matrixDetails: PricingPolicyMatrixDetail[]
) {
  const response: any = await axios.post(
    '/v2/prices/price-discount-matrices/details',
    matrixDetails
  );
  if (response.status === 201) return true;
  return false;
}

export async function getPricingPolicyByCustomerId(
  customerId: string
): Promise<PricingPolicy | null> {
  const response = await axios.get(`/v2/prices/pricing-policy/${customerId}`, {
    validateStatus: (status: number) => status === 404 || status == 200,
  });
  if (response.status === 404) return null;
  return validate(pricePolicySchema, response.data);
}

export async function getMaterialPriceFromPriceCatalog(
  materialId: string,
  pricingPolicyId: string
): Promise<{
  price: number;
  allowDiscount: boolean;
  allowManualPrice: boolean;
  allowZeroValue: boolean;
  image?: string;
} | null> {
  const response = await axios.get(
    `/v2/prices/price-catalogs/material-price/${materialId}/${pricingPolicyId}`,
    {
      validateStatus: (status: number) => status === 404 || status == 200,
    }
  );
  if (response.status === 404) return null;
  return validate(
    z.object({
      price: z.number(),
      allowDiscount: z.boolean(),
      allowManualPrice: z.boolean(),
      allowZeroValue: z.boolean(),
      image: z.string().optional(),
    }),
    response.data
  );
}

export async function getMaterialDiscounts(
  pricingPolicyId: string,
  materialId?: string,
  traderSiteId?: string | null
): Promise<PricingPolicyMatrixDetail[]> {
  const response = await axios.get(
    `/v2/prices/price-discount-matrices/material-discounts/${pricingPolicyId}/${materialId}/${traderSiteId}`,
    {
      validateStatus: (status: number) => status === 404 || status == 200,
    }
  );
  if (response.status === 404) return [];
  return validate(getPricingPolicyMatrixDetailsSchema, response.data.data);
}

export async function getMaterialVatFactorForTraderSite(
  traderSiteId: string,
  materialId: string
): Promise<VatFactor | undefined> {
  const response = await axios.get(`/v2/vat/factors/material/${materialId}/${traderSiteId}`, {
    validateStatus: (status: number) => status === 404 || status == 200,
  });
  if (response.status === 404) return undefined;
  return validate(vatFactorSchema, response.data);
}

export async function getExpenseVatFactorForTraderSite(
  traderSiteId: string,
  vatCategoryId: string
): Promise<number | undefined> {
  const response = await axios.get(`/v2/vat/factors/${vatCategoryId}/${traderSiteId}`, {
    validateStatus: (status: number) => status === 404 || status == 200,
  });
  if (response.status === 404) return undefined;
  return validate(z.number(), response.data);
}

export async function addAllCustomersToPricePolicy(pricingPolicyId: string) {
  const response = await axios.post(
    'v2/prices/pricing-policy/customers',
    {
      pricingPolicyId,
    },
    { validateStatus: (status: number) => status === 400 || status == 201 }
  );
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  return true;
}
